import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Alert, Box, Tab, Tabs } from '@mui/material';

import { Responsive } from 'components/content';
import { FullLoading } from 'components/loading';
import { Block, OneColumn } from 'components/page';
import { PermissionKey, usePermissionsContext } from 'context/PermissionsContext';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useQueryBi } from 'pages/bi/hooks';
import { ConfigBi } from 'pages/bi/types';

const BI_NAMES: Record<string, string> = {
    INICIO: 'Principal',
    VENDA: 'Vendas',
    ESTOQUE: 'Estoque',
    DIGITAL: 'Digital',
    SELLIN: 'Sell-in',
    REVENDA: 'Revenda',
};

export function BiPageComp() {
    const { hasPermission, isConfigured, isFetching: isFetchingPermissions } = usePermissionsContext();
    const navigate = useNavigate();
    const { isDownMd } = useBreakpoints();
    const { isSuccess: isSuccessBi, isError, isFetching: isFetchingBi, data = [] } = useQueryBi();
    const [activeBi, setActiveBi] = useState<ConfigBi | null>(null);
    const [permissionedBis, setPermissionedBis] = useState<ConfigBi[]>([]);

    /**
     * Ao carregar as permissões e BI's, constrói a lista de BI's filtrando os que o usuário tem permissão.
     * Define o BI ativo como o BI de início ou o primeiro da lista.
     * Caso não haja BI's disponíveis e o usuário tenha permissão para visualizar o comunicado,
     * redireciona para a página de comunicado.
     */
    useEffect(() => {
        if (isSuccessBi && isConfigured) {
            const filteredBis = data
                .map((item) => ({
                    ...item,
                    dsConfigBi: item.dsConfigBi.toUpperCase(),
                    dsConfigBiLabel: BI_NAMES[item.dsConfigBi.toUpperCase()] || item.dsConfigBi,
                }))
                .filter((item) => hasPermission([`BI_${item.dsConfigBi}_VISUALIZAR` as PermissionKey]));

            setPermissionedBis(filteredBis);
            setActiveBi(filteredBis.find((item) => item.dsConfigBi === 'INICIO') || filteredBis[0] || null);

            if (!filteredBis.length && hasPermission(['COMUNICADO_VISUALIZAR'])) {
                navigate('/comunicado');
            }
        }
    }, [isConfigured, isSuccessBi]);

    /**
     * Se está carregando os BI's ou as permissões, exibe o loading.
     */
    if (isFetchingBi || isFetchingPermissions) return <FullLoading loading={true} />;

    /**
     * Caso tenha falha na requisição dos BI's, exibe alerta de falha.
     */
    if (isError)
        return (
            <Alert sx={{ m: 2 }} severity="error">
                Falha ao carregar painéis de BI.
            </Alert>
        );

    /**
     * Caso a request não retorne nenhum BI, exibe alerta de panel não configurado.
     */
    if (!data.length)
        return (
            <Alert sx={{ m: 2 }} severity="warning">
                Painel BI não configurado.
            </Alert>
        );

    /**
     * Se não há BI ativo, o usuário não tem permissão para acessar nenhum BI.
     * Permanece na tela vazia ou redireciona para a página de comunicado.
     */
    if (!activeBi) return null;

    return (
        <OneColumn maxWidth={false} title="Indicadores">
            <Block sx={{ paddingTop: 1, gap: 2, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        allowScrollButtonsMobile
                        variant="scrollable"
                        scrollButtons="auto"
                        value={activeBi.dsConfigBi}
                        sx={{ '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 } }}
                        onChange={(_, value) => setActiveBi(permissionedBis.find((item) => item.dsConfigBi === value) as ConfigBi)}
                    >
                        {permissionedBis.map((item) => (
                            <Tab key={item.idConfigBi} label={item.dsConfigBiLabel} value={item.dsConfigBi} />
                        ))}
                    </Tabs>
                </Box>

                <Responsive height={activeBi.vlAltura}>
                    <iframe
                        title={activeBi.dsConfigBi}
                        id={`iframe_bi_${activeBi.idConfigBi}`}
                        src={isDownMd && activeBi.dsUrlMobile ? activeBi.dsUrlMobile : activeBi.dsUrl}
                    ></iframe>
                </Responsive>
            </Block>
        </OneColumn>
    );
}
